import React from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    TableSortLabel,
    Paper,
    Typography,
    LinearProgress,
    Tooltip,
    IconButton,
    Collapse,
    Box,
    Grid,
    CircularProgress,
    Button,
    Chip,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import {AdminDashboardServiceSingleton, BrandAdminServiceSingleton, BrowserServiceSingleton} from "src/Context";
import {
    PagedResponse,
    ReceiptLogListItem,
    ReceiptLogDetail,
    ReceiptLogQueryParams,
    SortField,
    TransactionStatus,
} from "../model/AdminDashboardTypes";
import {ReceiptLogFilters} from "./ReceiptLogFilters";

interface Filters {
    loanNumber: string;
    transactionStatus: TransactionStatus | "";
    paymentIntentId: string;
    chargeId: string;
    since: string;
    until: string;
    brandSlug: string;
}

interface State {
    loading: boolean;
    error: string | null;
    data: PagedResponse<ReceiptLogListItem> | null;
    page: number;
    size: number;
    sortDir: "asc" | "desc";
    sortField: SortField;
    filters: Filters;
    lastRefreshed: Date | null;
    expandedRowId: number | null;
    expandedDetail: ReceiptLogDetail | null;
    expandedLoading: boolean;
    expandedError: string | null;
    brands: { slug: string; displayName: string }[];
}

const STATUS_CONFIG: Record<TransactionStatus, { bg: string; color: string; border: string; dotClass: string }> = {
    PAID: {bg: "#e8f5e8", color: "#28a745", border: "#c3e6c3", dotClass: "status-dot--success"},
    PROCESSING: {bg: "#fffbeb", color: "#b45309", border: "#fde68a", dotClass: "status-dot--warning status-dot--pulse"},
    FAILED: {bg: "#fef2f2", color: "#c62828", border: "#fecaca", dotClass: "status-dot--danger"},
    INITIATED: {bg: "#E8F4F8", color: "#55575A", border: "#d0e8f0", dotClass: "status-dot--muted"},
};

const STATUS_DISPLAY: Record<TransactionStatus, string> = {
    PAID: "Paid",
    PROCESSING: "Processing",
    FAILED: "Failed",
    INITIATED: "Initiated",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
    APPRAISAL_FEE_LOAN: "Appraisal Fee",
    INTEREST_LOAN: "Interest",
};

const SORTABLE_COLUMNS: { field: SortField; label: string }[] = [
    {field: "initiatedTime", label: "Initiated Time"},
    {field: "paymentTime", label: "Payment Time"},
    {field: "loanNumber", label: "Loan Number"},
    {field: "borrowerFullName", label: "Borrower"},
    {field: "transactionStatus", label: "Status"},
    {field: "brandName", label: "Brand"},
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
// expand + ID + 6 sortable (incl. Initiated Time and Brand) + loan type + charge type + card +
// PI ID + action. Hand-maintained, and used as the colSpan for both the expanded detail row and
// the empty state — adding a column without bumping this leaves those two rows short.
const COL_COUNT = 13;

/**
 * Where the table keeps its query and scroll position for this browser tab, so opening a
 * payment's full details and coming back lands on the same filters, sort, page and place.
 */
const STATE_KEY = "simpl.admin.receiptLogTable.v1";

interface SavedState {
    page: number;
    size: number;
    sortDir: "asc" | "desc";
    sortField: SortField;
    filters: Filters;
    scrollY?: number;
}

function readSavedState(): Partial<SavedState> {
    try {
        const saved = JSON.parse(window.sessionStorage.getItem(STATE_KEY) || "null");
        if (!saved || typeof saved !== "object") return {};
        const valid: Partial<SavedState> = {};
        if (Number.isInteger(saved.page) && saved.page >= 0) valid.page = saved.page;
        if (PAGE_SIZE_OPTIONS.indexOf(saved.size) !== -1) valid.size = saved.size;
        if (saved.sortDir === "asc" || saved.sortDir === "desc") valid.sortDir = saved.sortDir;
        if (SORTABLE_COLUMNS.some((col) => col.field === saved.sortField)) valid.sortField = saved.sortField;
        if (saved.filters && typeof saved.filters === "object") valid.filters = saved.filters;
        if (typeof saved.scrollY === "number") valid.scrollY = saved.scrollY;
        return valid;
    } catch (e) {
        // Private windows and blocked storage throw; the table then simply starts fresh.
        return {};
    }
}

export class ReceiptLogTable extends React.Component<{}, State> {
    private readonly dashboardService = AdminDashboardServiceSingleton;
    private readonly browserService = BrowserServiceSingleton;
    private readonly brandService = BrandAdminServiceSingleton;

    // Restored before the state below, which builds on it.
    private readonly saved = readSavedState();
    private pendingScrollY: number | null = this.saved.scrollY !== undefined ? this.saved.scrollY : null;

    state: State = {
        loading: false,
        error: null,
        data: null,
        page: this.saved.page !== undefined ? this.saved.page : 0,
        size: this.saved.size || 20,
        sortDir: this.saved.sortDir || "desc",
        sortField: this.saved.sortField || "initiatedTime",
        filters: {
            loanNumber: "",
            transactionStatus: "",
            paymentIntentId: "",
            chargeId: "",
            since: "",
            until: "",
            brandSlug: "",
            ...(this.saved.filters || {}),
        },
        lastRefreshed: null,
        expandedRowId: null,
        expandedDetail: null,
        expandedLoading: false,
        expandedError: null,
        brands: [],
    };

    componentDidMount() {
        this.loadData();
        this.loadBrands();
    }

    /**
     * Fills the brand filter. Failing quietly is deliberate: the brand picker is a convenience,
     * and losing it must not stop the payments table from loading.
     */
    private loadBrands = () => {
        this.brandService.list()
            .then((brands) => this.setState({
                brands: brands.map((b) => ({slug: b.slug, displayName: b.displayName}))
            }))
            .catch(() => this.setState({brands: []}));
    };

    /** Saves the query being loaded, and optionally the scroll position, for this browser tab. */
    private persist = (scrollY?: number) => {
        try {
            const {page, size, sortDir, sortField, filters} = this.state;
            const saved: SavedState = {page, size, sortDir, sortField, filters};
            if (scrollY !== undefined) saved.scrollY = scrollY;
            window.sessionStorage.setItem(STATE_KEY, JSON.stringify(saved));
        } catch (e) {
            // Storage unavailable: the table still works, it just won't remember its place.
        }
    };

    private loadData = () => {
        this.persist();
        this.setState({loading: true, error: null});

        const {page, size, sortDir, sortField, filters} = this.state;
        const params: ReceiptLogQueryParams = {
            page,
            size,
            sort: sortDir,
            sortField,
            loanNumber: filters.loanNumber || undefined,
            transactionStatus: filters.transactionStatus || undefined,
            paymentIntentId: filters.paymentIntentId || undefined,
            chargeId: filters.chargeId || undefined,
            since: filters.since ? new Date(filters.since).toISOString() : undefined,
            until: filters.until ? new Date(filters.until).toISOString() : undefined,
            brandSlug: filters.brandSlug || undefined,
        };

        this.dashboardService
            .getReceiptLogs(params)
            .then((data) => {
                // Back from a payment's details: return to where the admin was, once the rows
                // that position belongs to have rendered.
                const restoreScrollY = this.pendingScrollY;
                this.pendingScrollY = null;
                this.setState({data, loading: false, lastRefreshed: new Date()}, () => {
                    if (restoreScrollY !== null) window.scrollTo(0, restoreScrollY);
                });
            })
            .catch((err) => {
                this.setState({
                    error: err.message || "Failed to load receipt logs",
                    loading: false,
                });
            });
    };

    private handleChangePage = (_event: unknown, newPage: number) => {
        this.setState({page: newPage, expandedRowId: null, expandedDetail: null}, this.loadData);
    };

    private handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({size: parseInt(event.target.value, 10), page: 0, expandedRowId: null, expandedDetail: null}, this.loadData);
    };

    private handleSort = (field: SortField) => () => {
        this.setState(
            (prev) => ({
                sortField: field,
                sortDir: prev.sortField === field
                    ? (prev.sortDir === "asc" ? "desc" : "asc")
                    : "desc",
                page: 0,
                expandedRowId: null,
                expandedDetail: null,
            }),
            this.loadData,
        );
    };

    private handleFilterChange = (filters: Filters) => {
        this.setState({filters});
    };

    private handleApplyFilters = () => {
        this.setState({page: 0, expandedRowId: null, expandedDetail: null}, this.loadData);
    };

    private handleClearFilters = () => {
        this.setState(
            {
                filters: {
                    loanNumber: "",
                    transactionStatus: "",
                    paymentIntentId: "",
                    chargeId: "",
                    since: "",
                    until: "",
                    brandSlug: "",
                },
                page: 0,
                expandedRowId: null,
                expandedDetail: null,
            },
            this.loadData,
        );
    };

    private handleRowToggle = (id: number) => {
        if (this.state.expandedRowId === id) {
            this.setState({expandedRowId: null, expandedDetail: null, expandedError: null});
            return;
        }

        this.setState({expandedRowId: id, expandedDetail: null, expandedLoading: true, expandedError: null});
        this.dashboardService
            .getReceiptLogDetail(id)
            .then((detail) => {
                this.setState({expandedDetail: detail, expandedLoading: false});
            })
            .catch((err) => {
                this.setState({
                    expandedError: err.message || "Failed to load details",
                    expandedLoading: false,
                });
            });
    };

    private handleViewFullDetails = (id: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        this.persist(window.scrollY);
        // Lets the details page's Back step back in history to this table, rather than push a
        // new entry that the browser's own Back button would then return to.
        this.browserService.redirectTo("/internal/receipt/" + id, {fromTable: true});
    };

    /** A time to show, or a dash when there is none (new Date(null) would be 1 Jan 1970). */
    private formatDate = (iso: string | null | undefined): string => {
        if (!iso) return "—";
        const d = new Date(iso);
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    private formatCurrency = (amount: number | null): string => {
        if (amount === null) return "\u2014";
        return "$" + amount.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };

    private formatTimeAgo = (date: Date): string => {
        const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
        if (seconds < 5) return "just now";
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m ago`;
    };

    private renderSortableHeader = (field: SortField, label: string) => {
        const {sortField, sortDir} = this.state;
        return (
            <TableCell key={field}>
                <TableSortLabel
                    active={sortField === field}
                    direction={sortField === field ? sortDir : "desc"}
                    onClick={this.handleSort(field)}
                >
                    {label}
                </TableSortLabel>
            </TableCell>
        );
    };

    private renderStatusBadge = (status: TransactionStatus) => {
        const config = STATUS_CONFIG[status];
        return (
            <span
                className="status-badge"
                style={{
                    backgroundColor: config.bg,
                    color: config.color,
                    border: `1px solid ${config.border}`,
                }}
            >
                <span className={`status-dot ${config.dotClass}`} />
                {STATUS_DISPLAY[status]}
            </span>
        );
    };

    private renderTruncatedId = (id: string | null) => {
        if (!id) return <span className="receipt-log-table__muted">{"\u2014"}</span>;
        const truncated = id.length > 20 ? id.slice(0, 8) + "\u2026" + id.slice(-8) : id;
        return (
            <Tooltip title={id} placement="top" interactive>
                <span className="receipt-log-table__monospace receipt-log-table__truncated-id">
                    {truncated}
                </span>
            </Tooltip>
        );
    };

    private renderPaginationInfo = (): string => {
        const {data, page, size} = this.state;
        if (!data || data.totalElements === 0) return "No results";
        const from = page * size + 1;
        const to = Math.min((page + 1) * size, data.totalElements);
        return `${from}\u2013${to} of ${data.totalElements.toLocaleString()}`;
    };

    private renderExpandedField = (label: string, value: React.ReactNode) => (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" className="expanded-row__label">
                {label}
            </Typography>
            <Typography variant="body2" className="expanded-row__value">
                {value || <span className="receipt-log-table__muted">{"\u2014"}</span>}
            </Typography>
        </Grid>
    );

    private renderExpandedContent = () => {
        const {expandedDetail, expandedLoading, expandedError, expandedRowId} = this.state;

        if (expandedLoading) {
            return (
                <div className="expanded-row__loading">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">Loading details...</Typography>
                </div>
            );
        }

        if (expandedError) {
            return (
                <div className="expanded-row__error">
                    <Typography variant="body2" color="error">{expandedError}</Typography>
                </div>
            );
        }

        if (!expandedDetail) return null;

        const detail = expandedDetail;
        const loan = detail.loan;

        return (
            <div className="expanded-row__content">
                {/* Receipt Details */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Receipt Details
                    </Typography>
                    <Grid container spacing={1}>
                        {this.renderExpandedField("Name on Card", detail.nameOnCard)}
                        {this.renderExpandedField("Agreed to Terms",
                            <span className={detail.agreedToTerms ? "expanded-row__bool--yes" : "expanded-row__bool--no"}>
                                {detail.agreedToTerms ? "Yes" : "No"}
                            </span>
                        )}
                        {this.renderExpandedField("Charge Types", detail.chargeType.join(", "))}
                        {this.renderExpandedField("Receipt",
                            detail.receiptUrl ? (
                                <a href={detail.receiptUrl} target="_blank" rel="noopener noreferrer" className="expanded-row__link">
                                    View Receipt
                                </a>
                            ) : null
                        )}
                    </Grid>
                </div>

                {/* Borrower */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Borrower
                    </Typography>
                    <Grid container spacing={1}>
                        {this.renderExpandedField("Full Name", loan.borrowerFullName)}
                        {this.renderExpandedField("Email",
                            loan.borrowerEmail
                                ? <a href={"mailto:" + loan.borrowerEmail} className="expanded-row__link">{loan.borrowerEmail}</a>
                                : null
                        )}
                        {this.renderExpandedField("Phone",
                            loan.borrowerPhone
                                ? <a href={"tel:" + loan.borrowerPhone} className="expanded-row__link">{loan.borrowerPhone}</a>
                                : null
                        )}
                    </Grid>
                </div>

                {/* Loan Officer */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Loan Officer
                    </Typography>
                    <Grid container spacing={1}>
                        {this.renderExpandedField("Full Name", loan.loanOfficerFullName)}
                        {this.renderExpandedField("Email",
                            loan.loanOfficerEmail
                                ? <a href={"mailto:" + loan.loanOfficerEmail} className="expanded-row__link">{loan.loanOfficerEmail}</a>
                                : null
                        )}
                        {this.renderExpandedField("Phone",
                            loan.loanOfficerPhone
                                ? <a href={"tel:" + loan.loanOfficerPhone} className="expanded-row__link">{loan.loanOfficerPhone}</a>
                                : null
                        )}
                    </Grid>
                </div>

                {/* Property */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Property
                    </Typography>
                    <Grid container spacing={1}>
                        {this.renderExpandedField("Address",
                            `${loan.propertyStreet}, ${loan.propertyCity}, ${loan.propertyState} ${loan.propertyZipCode}`
                        )}
                    </Grid>
                </div>

                {/* Loan Dates & Status */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Loan Dates
                    </Typography>
                    <Grid container spacing={1}>
                        {this.renderExpandedField("Loan Status", loan.loanStatus)}
                        {this.renderExpandedField("Application Signed", loan.applicationSignedDate)}
                        {this.renderExpandedField("ITP Date", loan.itpDate)}
                    </Grid>
                </div>

                {/* Financials */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Financials
                    </Typography>
                    <Grid container spacing={1}>
                        {loan.loanType === "APPRAISAL_FEE_LOAN" && (
                            <>
                                {this.renderExpandedField("Appraisal Fee", this.formatCurrency(loan.appraisalFee))}
                                {this.renderExpandedField("Credit Report Fee", this.formatCurrency(loan.creditReportFee))}
                                {this.renderExpandedField("Loan Amount", this.formatCurrency(loan.loanAmount))}
                            </>
                        )}
                        {loan.loanType === "INTEREST_LOAN" && (
                            <>
                                {this.renderExpandedField("Payment Amount", this.formatCurrency(loan.paymentAmount))}
                                {this.renderExpandedField("Payment Due", loan.paymentDue)}
                                {this.renderExpandedField("Payment Due Investor", loan.paymentDueInvestor)}
                            </>
                        )}
                    </Grid>
                </div>

                {/* Stripe Webhooks */}
                <div className="expanded-row__section">
                    <Typography variant="overline" className="expanded-row__section-title">
                        Stripe Webhook Events
                        {detail.stripeWebhookEvents.length > 0 && (
                            <span className="expanded-row__badge">{detail.stripeWebhookEvents.length}</span>
                        )}
                    </Typography>
                    {detail.stripeWebhookEvents.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No webhooks recorded.</Typography>
                    ) : (
                        <div className="expanded-row__webhook-table">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Event ID</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Payment Intent</TableCell>
                                        <TableCell>Processed</TableCell>
                                        <TableCell>Created</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {detail.stripeWebhookEvents.map((evt) => (
                                        <TableRow key={evt.id}>
                                            <TableCell className="receipt-log-table__monospace">{evt.eventId}</TableCell>
                                            <TableCell>
                                                <Chip label={evt.eventType} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell className="receipt-log-table__monospace">
                                                {evt.paymentIntentId || "\u2014"}
                                            </TableCell>
                                            <TableCell className="receipt-log-table__nowrap">
                                                {this.formatDate(evt.processedAt)}
                                            </TableCell>
                                            <TableCell className="receipt-log-table__nowrap">
                                                {this.formatDate(evt.createdAt)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                {/* View Full Details link */}
                <div className="expanded-row__footer">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={this.handleViewFullDetails(expandedRowId!)}
                    >
                        View Full Details
                    </Button>
                </div>
            </div>
        );
    };

    render() {
        const {loading, error, data, page, size, filters, lastRefreshed, expandedRowId} = this.state;

        return (
            <div className="receipt-log-table">
                <div className="receipt-log-table__toolbar">
                    <ReceiptLogFilters
                        filters={filters}
                        brands={this.state.brands}
                        onFilterChange={this.handleFilterChange}
                        onApply={this.handleApplyFilters}
                        onClear={this.handleClearFilters}
                    />
                    <div className="receipt-log-table__toolbar-right">
                        {lastRefreshed && (
                            <Typography variant="caption" color="textSecondary" className="receipt-log-table__last-refresh">
                                Updated {this.formatTimeAgo(lastRefreshed)}
                            </Typography>
                        )}
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={this.loadData} disabled={loading}>
                                <RefreshIcon fontSize="small" className={loading ? "receipt-log-table__spin" : ""} />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {error && (
                    <div className="receipt-log-table__error">
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    </div>
                )}

                <Paper className="receipt-log-table__paper" elevation={0} variant="outlined">
                    {loading && <LinearProgress className="receipt-log-table__progress" />}

                    <TableContainer className={loading ? "receipt-log-table__dimmed" : ""}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="receipt-log-table__expand-col" />
                                    <TableCell>ID</TableCell>
                                    {SORTABLE_COLUMNS.map((col) =>
                                        this.renderSortableHeader(col.field, col.label)
                                    )}
                                    <TableCell>Loan Type</TableCell>
                                    <TableCell>Charge Type</TableCell>
                                    <TableCell>Card</TableCell>
                                    <TableCell>Payment Intent ID</TableCell>
                                    <TableCell className="receipt-log-table__action-col" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data && data.content.length > 0 ? (
                                    data.content.map((row, idx) => {
                                        const isExpanded = expandedRowId === row.id;
                                        return (
                                            <React.Fragment key={row.id}>
                                                <TableRow
                                                    hover
                                                    className={
                                                        `receipt-log-table__row`
                                                        + (idx % 2 === 1 ? " receipt-log-table__row--striped" : "")
                                                        + (isExpanded ? " receipt-log-table__row--expanded" : "")
                                                    }
                                                    onClick={() => this.handleRowToggle(row.id)}
                                                >
                                                    <TableCell className="receipt-log-table__expand-col" padding="checkbox">
                                                        <IconButton size="small" aria-label="expand row">
                                                            {isExpanded
                                                                ? <KeyboardArrowUpIcon fontSize="small" />
                                                                : <KeyboardArrowDownIcon fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="receipt-log-table__id">#{row.id}</span>
                                                    </TableCell>
                                                    <TableCell className="receipt-log-table__nowrap">
                                                        {this.formatDate(row.initiatedTime)}
                                                    </TableCell>
                                                    <TableCell className="receipt-log-table__nowrap">
                                                        {this.formatDate(row.paymentTime)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="receipt-log-table__loan-number">{row.loanNumber}</span>
                                                    </TableCell>
                                                    <TableCell>{row.borrowerFullName}</TableCell>
                                                    <TableCell>
                                                        {this.renderStatusBadge(row.transactionStatus)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.brandName
                                                            ? row.brandName
                                                            : <span className="receipt-log-table__muted">{"\u2014"}</span>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="receipt-log-table__loan-type">
                                                            {LOAN_TYPE_LABELS[row.loanType] || row.loanType}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.chargeType.length > 0
                                                            ? row.chargeType.join(", ")
                                                            : <span className="receipt-log-table__muted">{"\u2014"}</span>}
                                                    </TableCell>
                                                    <TableCell className="receipt-log-table__nowrap">
                                                        {row.cardType && row.last4Digits
                                                            ? <span><span className="receipt-log-table__card-brand">{row.cardType}</span> ****{row.last4Digits}</span>
                                                            : <span className="receipt-log-table__muted">{"\u2014"}</span>}
                                                    </TableCell>
                                                    <TableCell>
                                                        {this.renderTruncatedId(row.paymentIntentId)}
                                                    </TableCell>
                                                    <TableCell className="receipt-log-table__action-col">
                                                        <OpenInNewIcon
                                                            className="receipt-log-table__row-arrow"
                                                            fontSize="small"
                                                            onClick={this.handleViewFullDetails(row.id)}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                {/* Expandable detail row */}
                                                <TableRow className="receipt-log-table__expand-row">
                                                    <TableCell
                                                        colSpan={COL_COUNT}
                                                        style={{paddingBottom: 0, paddingTop: 0}}
                                                    >
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <Box className="expanded-row">
                                                                {this.renderExpandedContent()}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    !loading && (
                                        <TableRow>
                                            <TableCell colSpan={COL_COUNT} align="center">
                                                <div className="receipt-log-table__empty">
                                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                                        No receipt logs found
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Try adjusting your filters or date range
                                                    </Typography>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <div className="receipt-log-table__footer">
                        <Typography variant="body2" color="textSecondary" className="receipt-log-table__info">
                            {this.renderPaginationInfo()}
                        </Typography>
                        <TablePagination
                            component="div"
                            count={data ? data.totalElements : 0}
                            page={page}
                            rowsPerPage={size}
                            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            labelRowsPerPage="Rows:"
                        />
                    </div>
                </Paper>
            </div>
        );
    }
}
